import * as httpx from "httpx"
import * as uuid from "uuid"
import { kernels } from "brandguard/api"
import { CancellablePromise } from "real-cancellable-promise"
import {
	TextKernelSettingsRequest,
	TextKernelSettingsResponse,
	TextKernelSearchRequest,
	TextKernelSearchResponse,
} from "brandguard/brandguard.text_pb"

import { TextKernelSetting, KernelMetadata, GrammarProvider, ProfanityProvider, RacismProvider } from "brandguard/brandguard.kernel_pb"

export namespace config {
	export function zero(d: Partial<TextKernelSetting> = {}): TextKernelSetting {
		return {
			id: "",
			threshold: 95,
			profanity: {
				enabled: false,
				provider: ProfanityProvider.GPT4O_MINI,
			},
			racism: {
				enabled: false,
				provider: RacismProvider.GPT4O_MINI,
			},
			grammar: {
				enabled: false,
				provider: GrammarProvider.GPT4O_MINI,
			},
			tnv: {
				enabled: false,
				prompt: "",
			},
			brand_voice: {
				enabled: true,
			},
			reference_rules: {
				enabled: false,
				rules: [],
			},
			brand_consistency: {
				enabled: true,
			},
			product_detection: {
				enabled: false,
			},
			...d,
		}
	}

	export function latest(
		bid: string,
		id: string,
		...options: httpx.option[]
	): CancellablePromise<TextKernelSettingsResponse> {
		return httpx.get(
			`${httpx.urlstorage.host()}/brands/${bid}/bg/text/kernel/${id}/settings/${uuid.NIL}`,
			{},
			...options,
		)
	}
}

export namespace searches {
	export function request(d: Partial<TextKernelSearchRequest> = {}): TextKernelSearchRequest {
		return {
			offset: 0n,
			brand_id: "",
			limit: 100n,
			...d,
		}
	}

	export function response(d: Partial<TextKernelSearchResponse> = {}): TextKernelSearchResponse {
		return {
			next: request(),
			items: [],
			...d,
		}
	}
}

export function search(
	bid: string,
	req: TextKernelSearchRequest,
	...options: httpx.option[]
): CancellablePromise<TextKernelSearchResponse> {
	return httpx
		.get<TextKernelSearchResponse>(`${httpx.urlstorage.host()}/brands/${bid}/bg/text/kernel/`, req, ...options)
		.then((r) => ({
			...r,
			items: (r.items || []).map(kernels.metadata.zero),
		}))
}

export function create_version(
	bid: string,
	id: string,
	req: TextKernelSettingsRequest,
	...options: httpx.option[]
): CancellablePromise<TextKernelSettingsResponse> {
	return httpx.post(`${httpx.urlstorage.host()}/brands/${bid}/bg/text/kernel/${id}/settings`, req, ...options)
}

export function rename(
	bid: string,
	id: string,
	version: string,
	name: string,
	...options: httpx.option[]
): CancellablePromise<KernelMetadata> {
	return httpx.patch(
		`${httpx.urlstorage.host()}/brands/${bid}/bg/text/kernel/${id}/name/${version}`,
		{ name },
		...options,
	)
}

export function reactivate(
	bid: string,
	id: string,
	version: string,
	...options: httpx.option[]
): CancellablePromise<KernelMetadata> {
	return httpx.post(
		`${httpx.urlstorage.host()}/brands/${bid}/bg/text/kernel/${id}/reactivate/${version}`,
		{},
		...options,
	)
}

export function getactive(
	bid: string,
	id: string,
	...options: httpx.option[]
): CancellablePromise<KernelMetadata | null> {
	return httpx
		.get<{ kernel?: KernelMetadata }>(
			`${httpx.urlstorage.host()}/brands/${bid}/bg/text/kernel/${id}/${uuid.NIL}`,
			{},
			...options,
		)
		.then((r) => r?.kernel || null)
		.catch(() => Promise.resolve(null))
}

export type { KernelMetadata }
